/* eslint-disable no-redeclare */
import { useCallback, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/shallow';
import { fetchLookerUserAttributes, fetchLookerUserInfo } from '../../../../lib/api/looker/api';
import { getLookerHostUrl, getUserConfigFromWindow } from '../../../../lib/lookerConfig';
import { type User, useUserStore } from '../../../../stores';

type UserDataKeys = keyof User;

type UserDataReturn<T = User> = {
  userData: T;
  saveUserData: () => Promise<void>;
  removeUserData: () => void;
  setUserEmail: (email: string) => void;
  setUser: (user: Partial<Omit<User, 'isInternalUser'>>) => void;
};

export function useUserData(): UserDataReturn<User>;
export function useUserData<K extends UserDataKeys>(selectKey: K): UserDataReturn<Pick<User, K>>;
export function useUserData<K extends UserDataKeys>(selectKeys: K[]): UserDataReturn<Pick<User, K>>;

export function useUserData<K extends UserDataKeys>(selectKeys?: K | K[]) {
  const userData = useUserStore(
    useShallow((state) => {
      if (selectKeys) {
        // Handle array of keys
        if (Array.isArray(selectKeys)) {
          return selectKeys.reduce(
            (acc, key) => {
              acc[key] = state[key];
              return acc;
            },
            {} as Record<K, User[K]>,
          ) as unknown as Pick<User, K>;
        }
        // Handle single key
        return { [selectKeys]: state[selectKeys] } as unknown as Pick<User, K>;
      }
      // Return all properties
      return {
        userID: state.userID,
        userEmail: state.userEmail,
        lookerURL: state.lookerURL,
        acctId: state.acctId,
        embeddedUserId: state.embeddedUserId,
        availableAdvertisers: state.availableAdvertisers,
        personalFolderId: state.personalFolderId,
        permissionFlag: state.permissionFlag,
        isInternalUser: state.isInternalUser,
      };
    }),
  );

  const userEmail = useUserStore((state) => state.userEmail);
  const setUserRef = useRef(useUserStore.getState().setUser);
  const clearUserDataRef = useRef(useUserStore.getState().clearUserData);

  const setUser = setUserRef.current;
  const clearUserData = clearUserDataRef.current;

  const saveUserData = useCallback(async () => {
    const lookerUrl = getLookerHostUrl();

    // Prefer window.LOOKER_CONFIG.user (populated by Angular host);
    // fall back to backend proxy endpoints if not present.
    const winUser = getUserConfigFromWindow();

    if (winUser) {
      setUser({
        userEmail: (userEmail ?? winUser.email ?? '')?.toLowerCase(),
        userID: winUser.userId,
        lookerURL: lookerUrl,
        acctId: winUser.accountId ?? '',
        embeddedUserId: winUser.embeddedUserId,
        personalFolderId: winUser.personalFolderId,
        permissionFlag: winUser.permissionFlag ?? 'Y',
      });
      return;
    }

    const [me, attrs] = await Promise.all([
      fetchLookerUserInfo(),
      fetchLookerUserAttributes(['userid', 'accountid', 'genome_enforce_permissions']),
    ]);

    if (!me) return;

    setUser({
      userEmail: (userEmail ?? me.email)?.toLowerCase(),
      userID: String(me.id),
      lookerURL: lookerUrl,
      acctId: attrs?.accountid ?? '',
      embeddedUserId: attrs?.userid ?? undefined,
      personalFolderId: me.personalFolderId,
      permissionFlag: attrs?.genome_enforce_permissions ?? 'Y',
    });
  }, [userEmail, setUser]);

  const setUserEmail = useCallback(
    (email: string) => {
      if (!email.trim()) {
        return;
      }

      setUser({
        userEmail: (userEmail || email)?.toLowerCase(),
      });
    },
    [userEmail, setUser],
  );

  const removeUserData = useCallback(() => {
    clearUserData();
  }, [clearUserData]);

  return useMemo(
    () => ({
      userData,
      saveUserData,
      removeUserData,
      setUserEmail,
      setUser,
    }),
    [userData, saveUserData, removeUserData, setUserEmail, setUser],
  );
}
